/**
 * Session Event Types & Infrastructure
 *
 * Defines TypeScript types for session lifecycle and progress events.
 * These types establish event schema contracts across backend and frontend
 * for real-time session tracking and progress monitoring.
 */
/**
 * Session event type enum
 * Defines all possible session-related events that can be emitted
 */
export var SessionEventType;
(function (SessionEventType) {
    /** Emitted when a new AI session is started */
    SessionEventType["SESSION_STARTED"] = "SESSION_STARTED";
    /** Emitted when an AI session is ended (completed or abandoned) */
    SessionEventType["SESSION_ENDED"] = "SESSION_ENDED";
    /** Emitted when a new execution phase begins */
    SessionEventType["SESSION_PHASE_STARTED"] = "SESSION_PHASE_STARTED";
    /** Emitted when an execution phase is completed */
    SessionEventType["SESSION_PHASE_COMPLETED"] = "SESSION_PHASE_COMPLETED";
    /** Emitted when work begins on a feature within a session */
    SessionEventType["SESSION_FEATURE_STARTED"] = "SESSION_FEATURE_STARTED";
    /** Emitted when a feature is completed within a session */
    SessionEventType["SESSION_FEATURE_COMPLETED"] = "SESSION_FEATURE_COMPLETED";
    /** Emitted when work begins on a task within a session */
    SessionEventType["SESSION_TASK_STARTED"] = "SESSION_TASK_STARTED";
    /** Emitted when a task is completed within a session */
    SessionEventType["SESSION_TASK_COMPLETED"] = "SESSION_TASK_COMPLETED";
    /** Emitted when progress is logged on a task within a session */
    SessionEventType["SESSION_TASK_PROGRESS"] = "SESSION_TASK_PROGRESS";
    /** Emitted when an error occurs during session execution */
    SessionEventType["SESSION_ERROR"] = "SESSION_ERROR";
})(SessionEventType || (SessionEventType = {}));
/**
 * Type guard to check if an event is a lifecycle event
 */
export function isSessionLifecycleEvent(event) {
    return (event.eventType === SessionEventType.SESSION_STARTED ||
        event.eventType === SessionEventType.SESSION_ENDED);
}
/**
 * Type guard to check if an event is a phase event
 */
export function isSessionPhaseEvent(event) {
    return (event.eventType === SessionEventType.SESSION_PHASE_STARTED ||
        event.eventType === SessionEventType.SESSION_PHASE_COMPLETED);
}
/**
 * Type guard to check if an event is a feature event
 */
export function isSessionFeatureEvent(event) {
    return (event.eventType === SessionEventType.SESSION_FEATURE_STARTED ||
        event.eventType === SessionEventType.SESSION_FEATURE_COMPLETED);
}
/**
 * Type guard to check if an event is a task event
 */
export function isSessionTaskEvent(event) {
    return (event.eventType === SessionEventType.SESSION_TASK_STARTED ||
        event.eventType === SessionEventType.SESSION_TASK_COMPLETED);
}
/**
 * Type guard to check if an event is a task progress event
 */
export function isSessionTaskProgressEvent(event) {
    return event.eventType === SessionEventType.SESSION_TASK_PROGRESS;
}
/**
 * Type guard to check if an event is an error event
 */
export function isSessionErrorEvent(event) {
    return event.eventType === SessionEventType.SESSION_ERROR;
}
//# sourceMappingURL=session-events.js.map